import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import type { CSSProperties } from 'react';

type PaginationData = {
	page?: number;
	from?: number;
	to?: number;
	total?: number;
	startDisabled?: boolean;
	endDisabled?: boolean;
};

type ResultsPaginationProps = {
	pagination: PaginationData;
	pageItemsCount?: number;
	onPrev: () => void;
	onNext: () => void;
	disabled?: boolean;
	align?: 'center' | 'start' | 'end';
	style?: CSSProperties;
};

function getTotalPages(pagination: PaginationData, pageCount: number) {
	const page = pagination.page ?? 1;
	const pageSize = page === 1
		? Math.max(1, pageCount)
		: Math.max(1, Math.round(((pagination.from ?? 1) - 1) / (page - 1)));
	return Math.max(1, Math.ceil((pagination.total ?? pageCount) / pageSize));
}

export const ResultsPagination = ({
	pagination,
	pageItemsCount,
	onPrev,
	onNext,
	disabled = false,
	align = 'center',
	style,
}: ResultsPaginationProps) => {
	const pageCount = pageItemsCount
		?? Math.max(0, (pagination.to ?? 0) - (pagination.from ?? 0) + 1);
	const totalResults = pagination.total ?? pageCount;
	const hasResults = totalResults > 0;

	if (!hasResults) {
		return (
			<div
				style={{
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					...style,
				}}
			>
				<span style={{ fontSize: 12, lineHeight: '22px', fontWeight: 500, color: '#64748b', userSelect: 'none' }}>
					No hay resultados
				</span>
			</div>
		);
	}

	const totalPages = getTotalPages(pagination, pageCount);
	const prevDisabled = disabled || !!pagination.startDisabled;
	const nextDisabled = disabled || !!pagination.endDisabled;
	const resultsLabel = pageCount < totalResults
		? `${pageCount} de ${totalResults} resultados`
		: `${totalResults} resultados`;

	return (
		<div
			style={{
				display: 'flex',
				justifyContent: align === 'center' ? 'center' : align === 'end' ? 'flex-end' : 'flex-start',
				alignItems: 'center',
				...style,
			}}
		>
			<div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
				<span style={{ fontSize: 12, lineHeight: '22px', whiteSpace: 'nowrap', userSelect: 'none', color: '#64748b' }}>
					{resultsLabel}
					<span style={{ color: '#64748b', margin: '0 6px' }}>-</span>
					<span style={{
						fontWeight: totalPages > 1 ? 600 : 500,
						color: totalPages > 1 ? '#334155' : '#64748b',
					}}>
						Página {pagination.page ?? 1} de {totalPages}
					</span>
				</span>
				<Button
					type='link'
					size='small'
					disabled={prevDisabled}
					style={{ padding: '0 4px', height: 22, color: prevDisabled ? '#cbd5e1' : '#475569' }}
					onClick={onPrev}
				>
					<LeftOutlined style={{ fontSize: 11 }} />
				</Button>
				<Button
					type='link'
					size='small'
					disabled={nextDisabled}
					style={{ padding: '0 4px', height: 22, color: nextDisabled ? '#cbd5e1' : '#475569' }}
					onClick={onNext}
				>
					<RightOutlined style={{ fontSize: 11 }} />
				</Button>
			</div>
		</div>
	);
};
